<template>
  <component
    :is="href ? 'router-link' : 'div'"
    :to="href || undefined"
    class="ip-party"
    :class="{ 'ip-party--empty': !id }"
  >
    <div class="ip-party__avatar">
      <img v-if="avatarSrc" :src="avatarSrc" :alt="displayName" />
      <i v-else-if="icon" :class="icon"></i>
      <span v-else>{{ initials }}</span>
    </div>
    <div class="ip-party__body">
      <div class="ip-party__label">{{ label }}</div>
      <div v-if="!id" class="ip-party__name ip-party__name--muted">
        {{ $t("itemProfile.partyMissing") }}
      </div>
      <div v-else-if="loading" class="ip-party__name ip-party__name--muted">
        <i class="fas fa-spinner fa-spin me-1"></i>
      </div>
      <template v-else>
        <div class="ip-party__name">{{ displayName }}</div>
        <div v-if="subtitle" class="ip-party__sub">{{ subtitle }}</div>
      </template>
    </div>
    <i v-if="href" class="fas fa-chevron-right ip-party__go"></i>
  </component>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import { getTranslatedListLabel } from "@/utils/listItemI18n";

/**
 * Una punta del movimiento de dinero (contacto o cuenta bancaria): con su logo
 * o iniciales, el nombre y un dato que la reconozca, y enlace a su ficha.
 */
export default {
  name: "ItemProfileParty",
  props: {
    /** id o documento ya poblado */
    value: { type: [String, Object], default: null },
    model: { type: String, required: true },
    label: { type: String, default: "" },
    lists: { type: Array, default: () => [] },
  },
  data() {
    return {
      doc: null,
      loading: false,
    };
  },
  computed: {
    id() {
      const v = this.value;
      if (!v) return null;
      return typeof v === "object" ? (v._id ? String(v._id) : null) : String(v);
    },
    isBank() {
      return this.model.toLowerCase() === "bankaccount";
    },
    icon() {
      if (!this.id) return "fas fa-question";
      return this.isBank ? "fas fa-university" : null;
    },
    href() {
      return this.id ? `/dynamic-model/${this.model}/view/${this.id}` : null;
    },
    displayName() {
      return (this.doc && this.doc.name) || "";
    },
    initials() {
      return String(this.displayName)
        .replace(/[^\p{L}\p{N}\s]/gu, " ")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
    },
    avatarSrc() {
      const raw = this.doc ? this.doc.image : null;
      const first = Array.isArray(raw) ? raw[0] : raw;
      if (!first) return null;
      if (typeof first === "string") return first;
      return first.url || first.path || first.src || null;
    },
    subtitle() {
      const d = this.doc;
      if (!d) return "";
      if (this.isBank) {
        return [d.bank, this.listLabel("currency", d.currency)].filter(Boolean).join(" · ");
      }
      return [this.listLabel("contactType", d.type), this.listLabel("country", d.country)]
        .filter(Boolean)
        .join(" · ");
    },
  },
  watch: {
    id: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    listLabel(listType, value) {
      if (!value) return "";
      const id = typeof value === "object" ? value._id : value;
      if (typeof value === "object" && (value.label || value.labels)) {
        return getTranslatedListLabel(value, this.$i18n && this.$i18n.locale);
      }
      const list = this.lists.find((l) => l.type === listType);
      const opt = list && (list.values || []).find((o) => String(o._id) === String(id));
      return opt ? getTranslatedListLabel(opt, this.$i18n && this.$i18n.locale) : "";
    },
    async load() {
      this.doc = null;
      if (!this.id) return;
      // La referencia poblada del item trae solo el nombre: se pide la ficha
      // completa (logo, tipo, país, banco, moneda). Mientras, se muestra el nombre.
      if (this.value && typeof this.value === "object" && this.value.name) {
        this.doc = this.value;
      } else {
        this.loading = true;
      }
      const id = this.id;
      try {
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/${encodeURIComponent(this.model)}/${encodeURIComponent(id)}`
        );
        if (id === this.id) this.doc = data;
      } catch (_e) {
        if (id === this.id) this.doc = { name: "" };
      } finally {
        if (id === this.id) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.ip-party {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.85rem 1rem;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.75rem;
  background-color: var(--bs-body-bg);
  color: inherit;
  text-decoration: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

a.ip-party:hover {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.12);
  color: inherit;
}

.ip-party--empty {
  border-style: dashed;
}

.ip-party__avatar {
  flex: 0 0 auto;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 0.8rem;
  border: 1px solid var(--bs-border-color);
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.05rem;
  overflow: hidden;
}

.ip-party__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ip-party__avatar i {
  font-size: 1.3rem;
}

.ip-party--empty .ip-party__avatar {
  color: var(--bs-secondary-color);
}

.ip-party__body {
  flex: 1 1 auto;
  min-width: 0;
}

.ip-party__label {
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--bs-secondary-color);
}

.ip-party__name {
  font-weight: 600;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ip-party__name--muted {
  color: var(--bs-secondary-color);
  font-weight: 400;
  font-style: italic;
}

.ip-party__sub {
  font-size: 0.8rem;
  color: var(--bs-secondary-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ip-party__go {
  color: var(--bs-secondary-color);
  font-size: 0.8rem;
}

#darkdesign .ip-party {
  background-color: var(--dark-bg-card);
  border-color: var(--dark-border-color);
}

#darkdesign .ip-party__avatar {
  background-color: var(--dark-bg-tertiary);
  border-color: var(--dark-border-color);
}

#darkdesign .ip-party__label,
#darkdesign .ip-party__sub,
#darkdesign .ip-party__go,
#darkdesign .ip-party__name--muted {
  color: var(--dark-text-secondary);
}
</style>
